import i18next from 'i18next';
import React, { useState } from 'react';

import {
  GENERIC_MODAL_BACK_BUTTON,
  GENERIC_MODAL_CANCEL_BUTTON,
  GENERIC_MODAL_NEXT_BUTTON,
  GENERIC_MODAL_SAVE_BUTTON,
} from '@commons/Modals/GenericModal/genericModalActions';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import { ListView } from '@commons/utils/styledLibraryComponents';
import EmptyState from '@commons/EmptyState';

import { getColumns as getCentralKitchensColumns } from '@admin/KitchenProducts/KitchenProductsProducts/components/creation/KitchenProductAssociatedCentralKitchensSection/utils/columns';
import KitchenProductSSPMSection from '@admin/KitchenProducts/KitchenProductsProducts/components/creation/KitchenProductSSPMSection';

import { ModalListViewContainer } from '../../styledComponents';

export const ADD_CENTRAL_KITCHENS_MODAL_STEPS = {
  CENTRAL_KITCHENS_SELECTION: 0,
  DELIVERED_STORES_CONFIGURATION: 1,
};

const CentralKitchensSelectionStep = (props) => {
  const {
    availableCentralKitchens,
    selectedCentralKitchens,
    storeName,
    setSelectedCentralKitchens,
  } = props;

  const clientStoreNamePlural = getClientStoreNameTranslation(storeName, true);

  // Computed once on mount so coming back from the second step restores the current selection,
  // without rebuilding the rows on every check (which would reset the ListView selection)
  const [centralKitchensRows] = useState(() => {
    const selectedCentralKitchenIds = selectedCentralKitchens.map(({ id }) => id);

    return availableCentralKitchens.map((centralKitchen) => ({
      ...centralKitchen,
      isRowSelected: selectedCentralKitchenIds.includes(centralKitchen.id),
    }));
  });

  if (!centralKitchensRows.length) {
    return <EmptyState />;
  }

  return (
    <ModalListViewContainer>
      <ListView
        columns={getCentralKitchensColumns(clientStoreNamePlural)}
        data={centralKitchensRows}
        defaultOrderBy={'name'}
        defaultOrderType={'asc'}
        forceEnableSelection={true}
        padding={'0px 24px 0px 24px'}
        setSelectedItems={setSelectedCentralKitchens}
      />
    </ModalListViewContainer>
  );
};

const DeliveredStoresConfigurationStep = (props) => {
  const { deliveredStores, storeName, addModalForm, referencePrice } = props;

  return (
    <ModalListViewContainer>
      <KitchenProductSSPMSection
        availableStores={deliveredStores}
        formFields={{ price: referencePrice }}
        productForm={addModalForm}
        storeName={storeName}
        isEmbeddedInModal
      />
    </ModalListViewContainer>
  );
};

/**
 * Two steps fullscreen modal mirroring the last steps of the central product creation: pick the
 * central kitchens to associate, then configure the delivered stores mappings (same SSPM section
 * as the creation flow, without the add and export actions).
 */
export const getAddCentralKitchensModalConfig = ({
  step,
  availableCentralKitchens,
  selectedCentralKitchens,
  setSelectedCentralKitchens,
  deliveredStores,
  addModalForm,
  referencePrice,
  storeName,
  handleNextStep,
  handlePreviousStep,
  handleSave,
}) => {
  const isCentralKitchensStep =
    step === ADD_CENTRAL_KITCHENS_MODAL_STEPS.CENTRAL_KITCHENS_SELECTION;

  const actions = isCentralKitchensStep
    ? [
        GENERIC_MODAL_CANCEL_BUTTON(),
        {
          ...GENERIC_MODAL_NEXT_BUTTON(),
          isDisabled: !selectedCentralKitchens.length,
          handleClick: handleNextStep,
          preventClosing: true,
        },
      ]
    : [
        {
          ...GENERIC_MODAL_BACK_BUTTON(),
          handleClick: handlePreviousStep,
          preventClosing: true,
        },
        {
          ...GENERIC_MODAL_SAVE_BUTTON(),
          handleClick: handleSave,
        },
      ];

  return {
    type: 'actionWhite',
    isFullscreen: true,
    icon: '/images/inpulse/add-black-small.svg',
    title: i18next.t('ADMIN.CENTRAL_PRODUCTS.ADD_CENTRAL_KITCHENS_MODAL_TITLE'),
    customPadding: '24px 0px 0px 0px',
    component: isCentralKitchensStep
      ? CentralKitchensSelectionStep
      : DeliveredStoresConfigurationStep,
    data: isCentralKitchensStep
      ? { availableCentralKitchens, selectedCentralKitchens, storeName, setSelectedCentralKitchens }
      : { deliveredStores, storeName, addModalForm, referencePrice },
    actions,
  };
};
